import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft,
  KeyRound,
  Copy,
  RefreshCw,
  Trash2,
  Loader2,
  ShieldAlert,
  CheckCircle2,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/settings/api-tokens")({
  component: ApiTokensPage,
  head: () => ({ meta: [{ title: "API tokens — MissionReady" }] }),
});

type Token = {
  id: string;
  name: string;
  token_prefix: string;
  scopes: string[];
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
};

const SCOPE_GROUPS: { group: string; scopes: { id: string; label: string }[] }[] = [
  {
    group: "Incidents",
    scopes: [
      { id: "incidents:read", label: "Read incidents" },
      { id: "incidents:write", label: "Create / update incidents" },
    ],
  },
  {
    group: "Officers & shifts",
    scopes: [
      { id: "officers:read", label: "Read officer roster & status" },
      { id: "shifts:read", label: "Read shifts & assignments" },
      { id: "shifts:write", label: "Create / modify assignments" },
    ],
  },
  {
    group: "Sites & patrols",
    scopes: [
      { id: "sites:read", label: "Read sites & geofences" },
      { id: "patrols:read", label: "Read patrols & tour runs" },
      { id: "patrols:write", label: "Trigger / close patrols" },
    ],
  },
  {
    group: "Tracking & telemetry",
    scopes: [
      { id: "tracking:read", label: "Read live GPS / locations" },
      { id: "tracking:write", label: "Ingest GPS pings" },
    ],
  },
  {
    group: "Webhooks & exports",
    scopes: [
      { id: "exports:read", label: "Download exports & reports" },
      { id: "webhooks:write", label: "Manage outbound webhooks" },
    ],
  },
];

const ALL_SCOPES = SCOPE_GROUPS.flatMap((g) => g.scopes.map((s) => s.id));

function generateRawToken(): string {
  // 32 random bytes → 64 char hex, prefixed for recognizability
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
  return `mr_live_${hex}`;
}

async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function ApiTokensPage() {
  const { user, currentOrg, currentRole } = useAuth();
  const canManage = currentRole === "org_owner" || currentRole === "admin";

  const [tokens, setTokens] = useState<Token[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Create form
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [expiresInDays, setExpiresInDays] = useState<string>("90");
  const [newSecret, setNewSecret] = useState<{ token: string; name: string } | null>(null);

  const refresh = useCallback(async () => {
    if (!currentOrg) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("api_tokens")
      .select("id,name,token_prefix,scopes,created_at,last_used_at,expires_at,revoked_at")
      .eq("organization_id", currentOrg.id)
      .order("created_at", { ascending: false });
    setLoading(false);
    if (error) toast.error(error.message);
    else setTokens((data ?? []) as Token[]);
  }, [currentOrg]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggleScope = (id: string, on: boolean) => {
    setScopes((prev) => (on ? Array.from(new Set([...prev, id])) : prev.filter((s) => s !== id)));
  };

  const selectAll = () => setScopes(ALL_SCOPES);
  const clearAll = () => setScopes([]);

  const createToken = async () => {
    if (!currentOrg || !user) return;
    if (!name.trim()) return toast.error("Give the token a name");
    if (scopes.length === 0) return toast.error("Pick at least one scope");
    setBusy(true);
    const raw = generateRawToken();
    const hash = await sha256Hex(raw);
    const prefix = raw.slice(0, 12); // mr_live_XXXX
    const days = Number(expiresInDays);
    const expires_at = Number.isFinite(days) && days > 0
      ? new Date(Date.now() + days * 24 * 3600 * 1000).toISOString()
      : null;
    const { error } = await supabase.from("api_tokens").insert({
      organization_id: currentOrg.id,
      name: name.trim(),
      token_prefix: prefix,
      token_hash: hash,
      scopes,
      created_by: user.id,
      expires_at,
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    setNewSecret({ token: raw, name: name.trim() });
    setName("");
    setScopes([]);
    refresh();
  };

  const revokeToken = async (id: string) => {
    if (!confirm("Revoke this token? Any service using it will lose access immediately.")) return;
    const { error } = await supabase
      .from("api_tokens")
      .update({ revoked_at: new Date().toISOString() })
      .eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Token revoked");
    refresh();
  };

  const deleteToken = async (id: string) => {
    if (!confirm("Delete this token record permanently?")) return;
    const { error } = await supabase.from("api_tokens").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Token deleted");
    refresh();
  };

  const rotateToken = async (t: Token) => {
    if (!currentOrg || !user) return;
    if (!confirm(`Rotate "${t.name}"? A new secret will be issued and the old one revoked.`)) return;
    setBusy(true);
    const raw = generateRawToken();
    const hash = await sha256Hex(raw);
    const prefix = raw.slice(0, 12);
    const expires_at = t.expires_at; // keep prior expiry
    const { error: insErr } = await supabase.from("api_tokens").insert({
      organization_id: currentOrg.id,
      name: t.name,
      token_prefix: prefix,
      token_hash: hash,
      scopes: t.scopes,
      created_by: user.id,
      expires_at,
    });
    if (insErr) {
      setBusy(false);
      return toast.error(insErr.message);
    }
    await supabase
      .from("api_tokens")
      .update({ revoked_at: new Date().toISOString() })
      .eq("id", t.id);
    setBusy(false);
    setNewSecret({ token: raw, name: `${t.name} (rotated)` });
    refresh();
  };

  const copy = async (value: string, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(label);
    } catch {
      toast.error("Could not copy");
    }
  };

  const active = useMemo(() => tokens.filter((t) => !t.revoked_at), [tokens]);
  const revoked = useMemo(() => tokens.filter((t) => t.revoked_at), [tokens]);

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <Link
        to="/settings"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to settings
      </Link>
      <div className="mt-3 flex items-center gap-3">
        <KeyRound className="h-5 w-5 text-primary" />
        <div>
          <h1 className="font-display text-2xl font-bold md:text-3xl">API tokens</h1>
          <p className="text-sm text-muted-foreground">
            {currentOrg?.name} · org-scoped secrets for integrations and automations.
          </p>
        </div>
      </div>

      {!canManage && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm">
          <ShieldAlert className="mt-0.5 h-4 w-4 text-muted-foreground" />
          <p>You can view this organization's tokens, but only owners and admins can create, rotate, or revoke them.</p>
        </div>
      )}

      {newSecret && (
        <div className="mt-6 rounded-xl border border-primary/40 bg-primary/5 p-5">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            <h3 className="font-display text-lg font-semibold">Copy your new token</h3>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            This is the only time the full secret for <span className="font-medium text-foreground">{newSecret.name}</span> will be shown. Store it somewhere safe.
          </p>
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-border bg-background p-3">
            <code className="flex-1 truncate font-mono text-sm">{newSecret.token}</code>
            <Button size="sm" variant="outline" onClick={() => copy(newSecret.token, "Token copied")}>
              <Copy className="mr-1 h-3.5 w-3.5" /> Copy
            </Button>
          </div>
          <Button className="mt-3" variant="ghost" size="sm" onClick={() => setNewSecret(null)}>
            I've saved it
          </Button>
        </div>
      )}

      {canManage && (
        <div className="mt-6 rounded-xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-semibold">Create a new token</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Tokens carry only the scopes you select and are bound to this organization.
          </p>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div>
              <Label htmlFor="tk-name" className="text-xs">Name</Label>
              <Input
                id="tk-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Zapier — incident sync"
                maxLength={80}
              />
            </div>
            <div>
              <Label htmlFor="tk-exp" className="text-xs">Expires in (days, blank = never)</Label>
              <Input
                id="tk-exp"
                type="number"
                min={1}
                max={3650}
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Scopes</Label>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" type="button" onClick={selectAll}>Select all</Button>
                <Button variant="ghost" size="sm" type="button" onClick={clearAll}>Clear</Button>
              </div>
            </div>
            <div className="mt-2 grid gap-3 md:grid-cols-2">
              {SCOPE_GROUPS.map((g) => (
                <div key={g.group} className="rounded-lg border border-border bg-background/40 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {g.group}
                  </div>
                  <div className="mt-2 space-y-2">
                    {g.scopes.map((s) => (
                      <label key={s.id} className="flex items-start gap-2 text-sm">
                        <Checkbox
                          checked={scopes.includes(s.id)}
                          onCheckedChange={(v) => toggleScope(s.id, !!v)}
                        />
                        <span>
                          <span className="block">{s.label}</span>
                          <code className="text-[10px] text-muted-foreground">{s.id}</code>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Button className="mt-5" onClick={createToken} disabled={busy}>
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <KeyRound className="mr-1 h-4 w-4" />}
            Generate token
          </Button>
        </div>
      )}

      <div className="mt-8">
        <h2 className="font-display text-lg font-semibold">Active tokens</h2>
        {loading ? (
          <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : active.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">No active tokens yet.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {active.map((t) => (
              <TokenRow
                key={t.id}
                t={t}
                canManage={canManage}
                onRotate={() => rotateToken(t)}
                onRevoke={() => revokeToken(t.id)}
                onCopyPrefix={() => copy(t.token_prefix + "…", "Prefix copied")}
              />
            ))}
          </div>
        )}
      </div>

      {revoked.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-lg font-semibold text-muted-foreground">Revoked</h2>
          <div className="mt-3 space-y-2">
            {revoked.map((t) => (
              <TokenRow
                key={t.id}
                t={t}
                canManage={canManage}
                revoked
                onDelete={() => deleteToken(t.id)}
                onCopyPrefix={() => copy(t.token_prefix + "…", "Prefix copied")}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TokenRow(props: {
  t: Token;
  canManage: boolean;
  revoked?: boolean;
  onRotate?: () => void;
  onRevoke?: () => void;
  onDelete?: () => void;
  onCopyPrefix: () => void;
}) {
  const { t, canManage, revoked, onRotate, onRevoke, onDelete, onCopyPrefix } = props;
  const expired = t.expires_at && new Date(t.expires_at) < new Date();
  return (
    <div className={`rounded-xl border p-4 ${revoked ? "border-border/60 bg-muted/20 opacity-70" : "border-border bg-card"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">{t.name}</span>
            {expired && !revoked && (
              <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-destructive">
                Expired
              </span>
            )}
            {revoked && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                Revoked
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            <code className="font-mono">{t.token_prefix}…</code>
            <button onClick={onCopyPrefix} className="hover:text-foreground" aria-label="Copy prefix">
              <Copy className="h-3 w-3" />
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {t.scopes.map((s) => (
              <code
                key={s}
                className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
              >
                {s}
              </code>
            ))}
          </div>
          <div className="mt-2 grid gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground sm:grid-cols-3">
            <span>Created {fmtDate(t.created_at)}</span>
            <span>Last used {fmtDate(t.last_used_at)}</span>
            <span>Expires {fmtDate(t.expires_at)}</span>
          </div>
        </div>
        {canManage && (
          <div className="flex shrink-0 gap-2">
            {!revoked && onRotate && (
              <Button variant="outline" size="sm" onClick={onRotate}>
                <RefreshCw className="mr-1 h-3.5 w-3.5" /> Rotate
              </Button>
            )}
            {!revoked && onRevoke && (
              <Button variant="outline" size="sm" onClick={onRevoke}>
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Revoke
              </Button>
            )}
            {revoked && onDelete && (
              <Button variant="ghost" size="sm" onClick={onDelete}>
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
